import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiJson } from './client';

export type OfflinePrefetch = 'OFF' | 'SETS' | 'FULL';
export type AutoSearchAfterVoice = 'OFF' | 'PROVIDER' | 'INTERNAL';

/** Per-account preferences that sync across all of a user's devices. */
export interface UserConfig {
  userId: string;
  theme: 'DARK' | 'LIGHT' | 'AUTO';
  linkTarget: 'BLANK' | 'SELF' | 'PARENT';
  searchProviderSlug: string;
  autoSearchAfterVoice: AutoSearchAfterVoice;
  internalAutocompleteEnabled: boolean;
}

/**
 * Per-device preferences. Null for any field means "use the SPA's
 * hard-coded default for this device." DeviceId is server-derived from
 * the request (md5(userAgent + ip)), so it's stable per browser/network
 * and the SPA never has to track it.
 */
export interface DeviceConfig {
  deviceId: string;
  iconSize: number | null;
  pageScale: number | null;
  itemSpacing: number | null;
  voiceSilenceDelayMs: number | null;
  offlinePrefetch: OfflinePrefetch | null;
  sessionShortTtlHours: number | null;
  sessionLongTtlDays: number | null;
}

// Hard-coded SPA defaults for unset device-config fields. The user-facing
// effective value is `dc.<field> ?? DEVICE_CONFIG_DEFAULTS.<field>`.
export const DEVICE_CONFIG_DEFAULTS = {
  iconSize: 64,
  pageScale: 1.0,
  itemSpacing: 20,
  voiceSilenceDelayMs: 2000,
  offlinePrefetch: 'FULL' as OfflinePrefetch,
  // Session TTL nulls fall through to the SystemSettings caps in
  // /api/auth/login; the SPA shows them as null too.
  sessionShortTtlHours: null as number | null,
  sessionLongTtlDays: null as number | null,
};

export function useUserConfig() {
  return useQuery({ queryKey: ['user-config'], queryFn: () => api.get('config').json<UserConfig>() });
}

export function useUpdateUserConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<UserConfig>) =>
      apiJson<UserConfig>('config', { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: (data) => qc.setQueryData(['user-config'], data),
  });
}

export function useDeviceConfig() {
  return useQuery({
    queryKey: ['device-config'],
    queryFn: () => api.get('config/device').json<DeviceConfig>(),
  });
}

export function useUpdateDeviceConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Omit<DeviceConfig, 'deviceId'>>) =>
      apiJson<DeviceConfig>('config/device', { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: (data) => qc.setQueryData(['device-config'], data),
  });
}
